import type { Entity } from '@backstage/catalog-model';

/**
 * The `spec.localDev` object describes how well a Component can be run
 * locally. It has two fields, each with its own fixed value set:
 *
 *   spec:
 *     localDev:
 *       docs: yes | no | poor | not-validated       # are local-dev docs available?
 *       status: works | issues | not-validated       # does it actually run locally?
 */
export const LOCAL_DEV_FIELDS = ['docs', 'status'] as const;
export type LocalDevField = (typeof LOCAL_DEV_FIELDS)[number];

/** Applied by the backend processor when a field is missing/invalid. */
export const NOT_VALIDATED = 'not-validated';

const GOOD = '#2e7d32';
const BAD = '#c62828';
const WARN = '#f9a825';
const NEUTRAL = '#616161';

type FieldConfig = {
  /** Sidebar/column label; rendered as "Local Dev: Docs" / "Local Dev". */
  label: string;
  /** Allowed values, in display order. */
  values: readonly string[];
  /** Human-readable label per value. */
  valueLabels: Record<string, string>;
  /** Chip colour per value. */
  colors: Record<string, string>;
};

export const LOCAL_DEV_CONFIG: Record<LocalDevField, FieldConfig> = {
  docs: {
    label: 'Docs',
    values: ['yes', 'no', 'poor', NOT_VALIDATED],
    valueLabels: {
      yes: 'Yes',
      no: 'No',
      poor: 'Poor',
      [NOT_VALIDATED]: 'Not validated',
    },
    colors: {
      yes: GOOD,
      no: BAD,
      poor: WARN,
      [NOT_VALIDATED]: NEUTRAL,
    },
  },
  status: {
    label: '',
    values: ['works', 'issues', NOT_VALIDATED],
    valueLabels: {
      works: 'Works',
      issues: 'Issues',
      [NOT_VALIDATED]: 'Not validated',
    },
    colors: {
      works: GOOD,
      issues: BAD,
      [NOT_VALIDATED]: NEUTRAL,
    },
  },
};

/** Column/filter heading: "Local Dev: Docs" for docs, "Local Dev" for status. */
export function localDevLabel(field: LocalDevField): string {
  const { label } = LOCAL_DEV_CONFIG[field];
  return label ? `Local Dev: ${label}` : 'Local Dev';
}

export function localDevValueLabel(
  field: LocalDevField,
  value: string,
): string {
  return LOCAL_DEV_CONFIG[field].valueLabels[value] ?? value;
}

export function localDevColor(field: LocalDevField, value: string): string {
  return LOCAL_DEV_CONFIG[field].colors[value] ?? NEUTRAL;
}

export function isLocalDevValue(field: LocalDevField, value: unknown): boolean {
  return (
    typeof value === 'string' && LOCAL_DEV_CONFIG[field].values.includes(value)
  );
}

/** Reads a single `spec.localDev.<field>` value off an entity, if known. */
export function getLocalDev(
  entity: Entity,
  field: LocalDevField,
): string | undefined {
  const localDev = (entity.spec as Record<string, unknown> | undefined)
    ?.localDev as Record<string, unknown> | undefined;
  const value = localDev?.[field];
  return isLocalDevValue(field, value) ? (value as string) : undefined;
}
